// CUSTODY — WALLETS (his custody redesign, yes on 2 Nov 2026; the drawing s6).
//
// Each wallet by its register name (N-091 — the same names the console's Wallets screen and the wallet register use): its
// balance, its limit, when it renews, and its one move at the right — Hot → Treasury, Refill Hot. The customer deposit
// addresses are one row (they sweep into Hot, never pay). Under it the payout addresses, by stage (Approved → Test sent →
// Whitelisted) with the one next step each (custody-admin/PayoutAddresses.tsx, drawn here).
//
// KEPT FROM today's balances: the read and its pace; listState (a failed refresh keeps the figures, said above them);
// poolTotal — nothing read is "Not known", never 0; freshness; the retired addresses still holding money (information);
// the two moves disabled with their reason when the wallet they need is not registered.
import { SigninQuiet } from '../../../components/v2/custody/SigninLine';
import { isCustodySignin } from '../../../lib/errors';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { WalletMovesSheet } from '../../../components/v2/custody/WalletMoves';
import { custody as api, type WalletBalance } from '../../../lib/api';
import { CLASS_WORD, money, relativeTime } from '../../../lib/custody-format';
import { aboveZero } from '../../../lib/custody-renewals';
import { listState, poolTotal } from '../../../lib/custody-screen';
import { DetailSheet, Panel, Register, SkeletonRows, Notice, EmptyState, StateLine, TAP } from '../../../components/v2/kit';
import { MoreDetails } from '../../../components/MoreDetails';
import { CustodyHead, CustodyTabs, CustodyActions, Figure, SectionTitle, clock } from '../../../components/v2/custody/parts';
import { CA } from '../../../components/v2/custody-admin/paths';
import { PayoutAddressesV2 } from '../custody-admin/PayoutAddresses';

type Row = { key: string; name: string; cls: string; rows: WalletBalance[] };
const ORDER = ['running_hot', 'dirty_hot', 'frozen', 'fee', 'treasury', 'deposit'];

/** A wallet's one name: the register's (name.full), else its class and network. */
const nameOf = (w: WalletBalance) => w.name?.full ?? `${CLASS_WORD[w.class] ?? w.class} · TRC20`;

export function WalletsCustodyV2() {
  const q = useQuery({ queryKey: ['custody', 'balances'], queryFn: api.balances, refetchInterval: 30_000 });
  const renewals = useQuery({ queryKey: ['custody', 'renewals'], queryFn: api.renewals, refetchInterval: 60_000, retry: false });
  const limits = useQuery({ queryKey: ['custody', 'limits'], queryFn: api.limits, staleTime: 30_000, retry: false });
  const state = listState(q);
  const wallets = q.data?.wallets ?? [];
  // ONE WALLET'S MOVES (the wallet's P-073, 3 Nov 2026): a wallet's name opens them (?wallet=); the deposit addresses' row opens their
  // list (?wallets=deposit), each with its own. In the URL, so Back closes what was opened.
  const [params, setParams] = useSearchParams();
  const setParam = (k: 'wallet' | 'wallets', v: string | null) => { const n = new URLSearchParams(params); if (v) n.set(k, v); else n.delete(k); setParams(n); };
  const openWallet = params.get('wallet') ? wallets.find((w) => String(w.id) === params.get('wallet')) ?? null : null;
  const depositList = params.get('wallets') === 'deposit';
  const hasHot = wallets.some((w) => w.class === 'running_hot');
  const hasVault = wallets.some((w) => w.class === 'treasury');
  const deposits = wallets.filter((w) => w.class === 'deposit');
  const flaggedHeld = useQuery({ queryKey: ['custody', 'flagged-held'], queryFn: api.flaggedHeld, staleTime: 10_000, retry: false });
  const deskInFlagged = (flaggedHeld.data as { desk?: { held: string } | null } | undefined)?.desk ?? null;
  const backFromFlagged = deskInFlagged !== null && Number(deskInFlagged.held) > 0;
  const rows: Row[] = [
    ...ORDER.filter((c) => c !== 'deposit').flatMap((c) => wallets.filter((w) => w.class === c).map((w) => ({ key: String(w.id), name: nameOf(w), cls: c, rows: [w] }))),
    ...(deposits.length ? [{ key: 'deposit', name: `Deposit · TRC20 · ${deposits.length} ${deposits.length === 1 ? 'customer' : 'customers'}`, cls: 'deposit', rows: deposits }] : []),
  ];
  const retired = (renewals.data?.renewals ?? []).flatMap((r) => r.retired.filter((x) => aboveZero(x.balance)).map((x) => ({ ...x, word: r.classWord })));
  const known = q.isSuccess && !q.isError;

  /** The balance and, under it, when it was read — or how much of it was read (never a part shown as the whole). */
  const balance = (r: Row) => {
    const t = poolTotal(r.rows);
    const latest = r.rows.map((x) => x.asOf).filter((x): x is string => !!x).sort().pop();
    return (
      <span className="block">
        <Figure value={t.total} asset={r.rows[0]!.asset} />
        <span className={`mt-1 block text-meta ${t.read < t.all ? 'text-warn' : 'text-tg-hint'}`}>{t.read < t.all ? `${t.read} of ${t.all} read` : latest ? `read ${clock(latest)}` : ''}</span>
      </span>
    );
  };
  const limit = (r: Row) => {
    const L = limits.data;
    // NO CAPS (db/0098) lifts the per-move caps on his word: the screen says so, never a number the signing route ignores.
    if ((r.cls === 'running_hot' || r.cls === 'dirty_hot' || r.cls === 'frozen') && L?.capsOff) return <span className="text-warn">No caps</span>;
    if (r.cls === 'running_hot' && L) return <><span className="block text-meta text-tg-hint">Per move</span><Figure value={L.moveCeilingUsdt} asset="USDTTRC" /></>;
    if ((r.cls === 'dirty_hot' || r.cls === 'frozen') && L?.sources?.[r.cls]) return <><span className="block text-meta text-tg-hint">Per move</span><Figure value={L.sources[r.cls].moveCeilingUsdt} asset="USDTTRC" /></>;
    if (r.cls === 'treasury' && r.rows[0]?.signing === 'multisig_2of3') return <span><span className="text-meta text-tg-hint">Keys </span>2 of 3</span>;
    if (r.cls === 'deposit') return <span className="text-meta text-tg-hint">Swept by its rating</span>;
    if (r.cls === 'fee') return <span className="text-meta text-tg-hint">Chain fees</span>;
    return <span className="text-tg-hint">—</span>;
  };
  const renews = (r: Row) => {
    const p = renewals.data?.renewals.find((x) => x.class === r.cls);
    if (!p || p.dueInDays === null) return <span className="text-tg-hint">—</span>;
    return p.dueInDays === 0 ? 'due now' : `in ${p.dueInDays} ${p.dueInDays === 1 ? 'day' : 'days'}`;
  };
  // FLAGGED BACK TO HOT (the wallet's item 5, 3 Nov 2026): on the first Flagged row, when the desk has money of its own there.
  const firstFlagged = rows.find((r) => r.cls === 'frozen')?.key;
  const openRow = (r: Row) => (r.cls === 'deposit' ? setParam('wallets', 'deposit') : setParam('wallet', String(r.rows[0]!.id)));
  const move = (r: Row) => (r.cls === 'frozen' && r.key === firstFlagged && backFromFlagged && hasHot
    ? <Link to="/custody/return" className="btn-secondary whitespace-nowrap px-3 text-body">Back to Hot</Link>
    : r.cls === 'running_hot'
    ? (hasVault ? <Link to="/custody/move" className="btn-secondary whitespace-nowrap px-3 text-body">To Treasury</Link> : <button type="button" className="btn-secondary whitespace-nowrap px-3 text-body" disabled title="No Treasury wallet is registered on this chain">To Treasury</button>)
    : r.cls === 'treasury'
      ? (hasHot ? <Link to="/custody/refill" className="btn-secondary whitespace-nowrap px-3 text-body">Refill Hot</Link> : <button type="button" className="btn-secondary whitespace-nowrap px-3 text-body" disabled title="No Hot is registered on this chain">Refill Hot</button>)
      : null);

  return (
    <div className="lg:max-w-[1160px]">
      <CustodyHead actions={<CustodyActions items={[{ label: 'Propose payout address', to: CA.propose }, { label: 'Move', to: '/custody/move' }, { label: 'Pay out', to: '/custody/pay' }, { label: 'Wallet register', to: '/wallets' }]} />} />
      <CustodyTabs current="wallets" />
      {state.show === 'failed' ? (
        isCustodySignin(q.error) ? <SigninQuiet error={q.error} /> : <Notice tone="bad" action={<button type="button" className={TAP} onClick={() => void q.refetch()}>Try again</button>}>Couldn&apos;t read the balances.</Notice>
      ) : (
        <>
          {state.stale && !isCustodySignin(q.error) && <Notice action={<button type="button" className={TAP} onClick={() => void q.refetch()}>Try again</button>}>Couldn&apos;t refresh — showing the last read.</Notice>}
          {q.data && q.data.freshness !== 'fresh' && wallets.length > 0 && (
            <Notice tone="neutral">Figures not current{q.data.asOf ? ` — oldest reading ${relativeTime(q.data.asOf)}` : ''}.</Notice>
          )}
          <Panel flush>
            {state.show === 'pending' ? <SkeletonRows rows={4} what="the wallets" />
              : rows.length === 0 ? (q.isError ? <p className="px-4 py-10 text-center text-body text-tg-hint">Not known — the last read failed.</p> : (
                <EmptyState className="py-10" action={<Link to="/custody/settings" className="btn-secondary px-4 text-body">Register the Ledger</Link>}>No wallet on this chain yet.</EmptyState>
              )) : (
                <Register<Row>
                  label="Custody wallets"
                  rows={rows}
                  rowKey={(r) => r.key}
                  columns={[
                    { key: 'name', head: 'Wallet', cell: (r) => <NameButton r={r} onOpen={openRow} /> },
                    { key: 'bal', head: 'Balance', align: 'right', width: 'w-[14rem]', cell: balance },
                    { key: 'limit', head: 'Limit', align: 'right', width: 'w-[10.5rem]', cell: limit },
                    { key: 'renews', head: 'Renews', align: 'right', width: 'w-[6rem]', cell: renews },
                    { key: 'move', head: '', align: 'right', width: 'w-[9.5rem]', cell: (r) => move(r) },
                  ]}
                  card={(r) => ({ title: <NameButton r={r} onOpen={openRow} />, figure: balance(r), meta: <span className="flex flex-wrap gap-x-4 gap-y-1"><span>{limit(r)}</span><span><span className="text-tg-hint">Renews </span>{renews(r)}</span></span>, state: move(r) ?? undefined })}
                />
              )}
          </Panel>
          {known && retired.length > 0 && (
            <div className="mt-3">
              {/* words:ok — the class word and the figure are the renewal read's own */}
              <StateLine more={<ul className="space-y-1">{retired.map((x) => <li key={x.walletId}>{x.name?.full ?? `${x.word} · ${x.label ?? ''}`} · {money(x.balance!, 'USDTTRC')}</li>)}</ul>}>
                {retired.length === 1 ? '1 retired address still holds money' : `${retired.length} retired addresses still hold money`}
              </StateLine>
            </div>
          )}
        </>
      )}

      {known && wallets.length > 0 && (
        <div className="mt-3">
          <MoreDetails label="What each wallet is">
            <ul className="space-y-1 text-meta text-tg-hint">
              {/* Read from the wallet's own rule (orchestrator/src/kyt/route.mjs, contracts/wallet.mjs KYT_RESULT_POOL) — no
                  score routes money; KYT never sends a deposit to Flagged (Astra, 2 Nov 2026). */}
              <li>Hot — clean money. Pays customers and merchants.</li>
              <li>Merchant Hot — exposed money: criminal-type funds traced $100+ direct or $1,000+ indirect, or a sanctions listing traced $1,000+ and 1%+ of the payment. Pays merchants only.</li>
              <li>Flagged — never routed by KYT; holds what you move there. Moves on your decision.</li>
              <li>Treasury — the bulk. Refills Hot.</li>
              <li>Fee — pays activation and energy, in TRX. Never holds USDT.</li>
              <li>Deposit — one address per customer. Swept out, never paid from.</li>
            </ul>
          </MoreDetails>
        </div>
      )}

      <div id="payouts"><SectionTitle title="Payout addresses" /></div>
      <PayoutAddressesV2 embedded />
      {openWallet && <WalletMovesSheet w={openWallet} head={<WalletHead w={openWallet} />} onBack={depositList ? () => setParam('wallet', null) : null} onClose={() => { const n = new URLSearchParams(params); n.delete('wallet'); n.delete('wallets'); setParams(n); }} />}
      {!openWallet && depositList && (
        <DetailSheet title="Deposit · TRC20" onClose={() => setParam('wallets', null)}>
          <ul>
            {wallets.filter((w) => w.class === 'deposit').map((w) => (
              <li key={w.id} className="flex items-center justify-between gap-3 border-b border-stroke-soft py-3 last:border-b-0">
                <span className="min-w-0">
                  <span className="block break-words text-body font-medium">{w.name?.full ?? `${CLASS_WORD[w.class] ?? w.class} · TRC20`}</span>
                  <span className="mt-0.5 block text-meta text-tg-hint"><Figure value={w.balance} asset={w.asset} /></span>
                </span>
                <button type="button" className={`${TAP} shrink-0`} onClick={() => setParam('wallet', String(w.id))}>Moves →</button>
              </li>
            ))}
          </ul>
        </DetailSheet>
      )}
    </div>
  );
}

/** A wallet's name in the register, as the way to its moves. */
function NameButton({ r, onOpen }: { r: Row; onOpen: (r: Row) => void }) {
  return (
    <button type="button" onClick={() => onOpen(r)} className="line-clamp-2 break-words text-left font-medium hover:underline">{r.name}</button>
  );
}

/** The opened wallet above its moves: its balance as last read, and its whole address. */
function WalletHead({ w }: { w: WalletBalance }) {
  return (
    <div className="border-b border-stroke-soft pb-3">
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-meta text-tg-hint">{w.asOf ? `Read ${relativeTime(w.asOf)}` : 'Not read'}</span>
        <span className="font-mono tabular text-body"><Figure value={w.balance} asset={w.asset} /></span>
      </div>
      <p className="mt-1 break-all font-mono text-meta">{w.address}</p>
    </div>
  );
}
